import React, { useState } from "react";

import { useQueryClient } from "@tanstack/react-query";

import { updateBankingDetails } from "services";

import { incompleteMessage, useGetBankingDetails, useGetFarmDetails, useGetFarms, useTranslation } from "utils";

import { GET_BANKING_DETAILS, GET_FARM_DATA_SUBMISSION_STATUS, GET_FARM_DETAILS } from "consts";

import { UpdateBankingDetailsRequest } from "models";

import { Button, Drawer, ErrorBoundary, ExtraInfo, Form, Typography, Message as message } from "components";

import { AddressInformation } from "pages/app/wallet/components/AddressInformation";
import { BankInformation } from "pages/app/wallet/components/BankInformation";

type BankingDetailsFormValues = Omit<UpdateBankingDetailsRequest, "addressStreet">;

type BankingDetailsReviewDrawerProps = {
  visible: boolean;
  onClose: () => void;
};

export const BankingDetailsReviewDrawer = ({ visible, onClose }: BankingDetailsReviewDrawerProps) => {
  const { t } = useTranslation(["points", "onboarding"]);
  const queryClient = useQueryClient();
  const [form] = Form.useForm<BankingDetailsFormValues>();
  const { currentFarm } = useGetFarms();
  const { data: bankingDetails, isLoading: isBankingDetailsLoading } = useGetBankingDetails({ farmId: currentFarm?.id });
  const { data: farmDetails, isLoading: isFarmDetailsLoading } = useGetFarmDetails({ farmId: currentFarm?.id });

  const [isSaving, setIsSaving] = useState(false);
  // null until the farmer edits the street, so the saved street is kept
  const [editedAddressStreet, setEditedAddressStreet] = useState<string | null>(null);
  const [isTaxRateInfoVisible, setIsTaxRateInfoVisible] = useState(false);
  const [isFarmNumberInfoVisible, setIsFarmNumberInfoVisible] = useState(false);

  const addressStreet = editedAddressStreet ?? bankingDetails?.addressStreet ?? "";
  const isLoading = isBankingDetailsLoading || isFarmDetailsLoading;
  // a failed load already gets the global error toast; saving without the form would do nothing
  const isFormReady = !!bankingDetails && !!farmDetails;

  const handleClose = () => {
    setEditedAddressStreet(null);
    onClose();
  };

  const handleSave = (values: BankingDetailsFormValues) => {
    setIsSaving(true);

    updateBankingDetails({ ...values, addressStreet })
      .then(
        () => {
          queryClient.invalidateQueries({ queryKey: [GET_BANKING_DETAILS] });
          queryClient.invalidateQueries({ queryKey: [GET_FARM_DATA_SUBMISSION_STATUS] });
          queryClient.invalidateQueries({ queryKey: [GET_FARM_DETAILS] });
          message({ content: t("bankingSaved"), icon: "tick" });
          handleClose();
        },
        // a failed save: the global axios handler shows the error toast; the drawer stays open
        () => {}
      )
      .finally(() => {
        setIsSaving(false);
      });
  };

  return (
    <>
      <Drawer
        className="banking-details-review-drawer"
        dataTestId="banking-details-review-drawer"
        title={t("bankingHeader.update")}
        visible={visible}
        onClose={handleClose}
        loading={isLoading}
        footer={
          <Button
            block
            type="primary"
            disabled={!isFormReady || isSaving}
            loading={isSaving}
            onClick={() => form.submit()}
            data-testid="banking-details-review-save"
          >
            {t("bankingUpdateButton")}
          </Button>
        }
      >
        {isFormReady && (
          <Form
            form={form}
            name="bankingDetailsReview"
            preserve={false}
            onFinish={handleSave}
            onFinishFailed={() => incompleteMessage(t("errors.mandatoryFields"))}
          >
            <Typography variant="h1" className="margin-bottom-350">
              {t("bankingHeadline")}
            </Typography>
            <ErrorBoundary>
              <BankInformation details={bankingDetails} setToggleModalTaxRate={setIsTaxRateInfoVisible} toggleModalTaxRate={isTaxRateInfoVisible} />
            </ErrorBoundary>
            <ErrorBoundary>
              <AddressInformation
                addressStreet={addressStreet}
                details={bankingDetails}
                farmCode={farmDetails.code}
                setAddressStreet={setEditedAddressStreet}
                setToggleModalFarmNumber={setIsFarmNumberInfoVisible}
                toggleModalFarmNumber={isFarmNumberInfoVisible}
                t={t}
              />
            </ErrorBoundary>
          </Form>
        )}
      </Drawer>
      <ExtraInfo setVisible={setIsTaxRateInfoVisible} type="taxRate" visible={isTaxRateInfoVisible} />
      <ExtraInfo setVisible={setIsFarmNumberInfoVisible} type="eu-farm-number-explanation" visible={isFarmNumberInfoVisible} />
    </>
  );
};
